import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Building2, Mail, Phone, Plus, Edit, Trash2, Star, Briefcase, MapPin, Calendar, Users, ExternalLink } from 'lucide-react';
import RecruiterLayout from '@/components/RecruiterLayout';
import ClientContactForm from '@/components/ClientContactForm';
import JobForm from '@/components/JobForm';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

const ClientDetail = () => {
  const { clientId } = useParams();
  const [client, setClient] = useState<any>(null);
  const [contacts, setContacts] = useState<any[]>([]);
  const [activeJobs, setActiveJobs] = useState<any[]>([]);
  const [jobHistory, setJobHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [jobFormOpen, setJobFormOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (clientId) {
      fetchClientDetails();
    }
  }, [clientId]);

  const fetchClientDetails = async () => {
    try {
      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('*')
        .eq('id', clientId)
        .single();

      if (clientError) throw clientError;
      setClient(clientData);

      const { data: contactsData, error: contactsError } = await supabase
        .from('client_contacts')
        .select('*')
        .eq('client_id', clientId)
        .order('is_primary', { ascending: false });

      if (contactsError) throw contactsError;
      setContacts(contactsData || []);

      // Fetch active jobs (draft, published)
      const { data: activeJobsData } = await supabase
        .from('jobs')
        .select(`
          *,
          assigned_to_user:app_users!jobs_assigned_to_fkey(full_name),
          applications:candidate_applications(count)
        `)
        .eq('client_id', clientId)
        .in('status', ['draft', 'published'])
        .order('created_at', { ascending: false });

      setActiveJobs(activeJobsData || []);

      // Fetch job history (closed, archived)
      const { data: historyData } = await supabase
        .from('jobs')
        .select(`
          *,
          assigned_to_user:app_users!jobs_assigned_to_fkey(full_name),
          applications:candidate_applications(count)
        `)
        .eq('client_id', clientId)
        .in('status', ['closed', 'archived'])
        .order('updated_at', { ascending: false });

      setJobHistory(historyData || []);

    } catch (error) {
      console.error('Error fetching client details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContact = async (id: string) => {
    if (!confirm('Are you sure you want to delete this contact?')) return;
    try {
      const { error } = await supabase.from('client_contacts').delete().eq('id', id);
      if (error) throw error;
      toast({ title: 'Contact deleted successfully' });
      fetchClientDetails();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'bg-green-500/10 text-green-600 border-green-500/20';
      case 'draft': return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
      case 'closed': return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
      case 'archived': return 'bg-red-500/10 text-red-600 border-red-500/20';
      default: return '';
    }
  };

  const JobCard = ({ job }: { job: any }) => (
    <Card className="p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold">{job.title}</h3>
            <Badge variant="outline" className={getStatusColor(job.status)}>
              {job.status}
            </Badge>
          </div>
          <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
            {job.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {job.location}
              </span>
            )}
            {job.job_type && (
              <span className="capitalize">{job.job_type.replace('_', ' ')}</span>
            )}
            {job.work_mode && (
              <span className="capitalize">{job.work_mode}</span>
            )}
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => navigate(`/recruiter/jobs/${job.id}/kanban`)}
        >
          <ExternalLink className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1 text-muted-foreground">
            <Users className="h-3 w-3" />
            {job.applications?.[0]?.count || 0} candidates
          </span>
          {job.assigned_to_user && (
            <span className="text-muted-foreground">
              Assigned: {job.assigned_to_user.full_name}
            </span>
          )}
        </div>
        <span className="text-muted-foreground flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {format(new Date(job.created_at), 'MMM d, yyyy')}
        </span>
      </div>

      {job.salary_min && job.salary_max && !job.hide_salary && (
        <div className="mt-2 text-sm text-muted-foreground">
          {job.salary_currency} {job.salary_min.toLocaleString()} - {job.salary_max.toLocaleString()}
        </div>
      )}
    </Card>
  );

  if (loading) {
    return (
      <RecruiterLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </RecruiterLayout>
    );
  }

  if (!client) {
    return (
      <RecruiterLayout>
        <Card className="p-12 text-center">
          <h3 className="text-xl font-semibold mb-2">Client not found</h3>
          <Button onClick={() => navigate('/recruiter/clients')}>
            Back to Clients
          </Button>
        </Card>
      </RecruiterLayout>
    );
  }

  return (
    <RecruiterLayout>
      <div className="space-y-6">
        <Card className="p-6">
          <div className="flex items-start gap-6">
            <div className="p-4 bg-primary/10 rounded-lg">
              <Building2 className="h-12 w-12 text-primary" />
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold">{client.name}</h1>
                {client.is_active ? (
                  <Badge variant="default">Active</Badge>
                ) : (
                  <Badge variant="secondary">Inactive</Badge>
                )}
              </div>
              
              {client.company_name && (
                <p className="text-lg text-muted-foreground mb-2">{client.company_name}</p>
              )}

              {client.category && (
                <Badge variant="outline" className="mb-3">
                  {client.category.replace('_', ' ')}
                </Badge>
              )}
              
              {client.notes && (
                <p className="text-muted-foreground mt-4">{client.notes}</p>
              )}
            </div>

            <div className="flex gap-2">
              <Button onClick={() => setJobFormOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Job
              </Button>
            </div>
          </div>
        </Card>

        <Tabs defaultValue="jobs" className="space-y-4">
          <TabsList>
            <TabsTrigger value="jobs" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Active Jobs ({activeJobs.length})
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Job History ({jobHistory.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Contacts ({contacts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="jobs">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Active Job Openings</h2>
                <Button onClick={() => setJobFormOpen(true)} size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  New Job
                </Button>
              </div>

              {activeJobs.length === 0 ? (
                <div className="text-center py-12">
                  <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">No active job openings</p>
                  <Button onClick={() => setJobFormOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create First Job
                  </Button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {activeJobs.map(job => (
                    <JobCard key={job.id} job={job} />
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-6">Job History</h2>

              {jobHistory.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No closed or archived jobs</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {jobHistory.map(job => (
                    <JobCard key={job.id} job={job} />
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="contacts">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Contact Persons</h2>
                <Button onClick={() => { setSelectedContact(null); setContactFormOpen(true); }}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Contact
                </Button>
              </div>

              {contacts.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No contacts added yet
                </p>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {contacts.map(contact => (
                    <Card key={contact.id} className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{contact.name}</h3>
                          {contact.is_primary && (
                            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedContact(contact); setContactFormOpen(true); }}>
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteContact(contact.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      
                      {contact.position && (
                        <p className="text-sm text-muted-foreground mb-2">{contact.position}</p>
                      )}

                      <div className="space-y-1">
                        {contact.email && (
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            <span>{contact.email}</span>
                          </div>
                        )}
                        {contact.phone && (
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="h-3 w-3 text-muted-foreground" />
                            <span>{contact.phone}</span>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>

        <ClientContactForm
          clientId={clientId!}
          contact={selectedContact}
          open={contactFormOpen}
          onOpenChange={setContactFormOpen}
          onSuccess={() => {
            fetchClientDetails();
            setContactFormOpen(false);
          }}
        />

        <JobForm
          job={{ client_id: clientId }}
          open={jobFormOpen}
          onOpenChange={setJobFormOpen}
          onSuccess={() => {
            fetchClientDetails();
            setJobFormOpen(false);
          }}
        />
      </div>
    </RecruiterLayout>
  );
};

export default ClientDetail;
